import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './LMSStatus.css';

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v) {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleDateString('th-TH', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return v;
  }
}

function PaymentStatusBadge({ status }) {
  const map = {
    PAID: 'badge-paid',
    PENDING: 'badge-pending',
    NONE: 'badge-none',
    OVERDUE: 'badge-overdue',
  };
  return (
    <span className={`lms-badge ${map[status] || 'badge-none'}`}>{status || '-'}</span>
  );
}

/**
 * L.11 LMS status lookup.
 * - Standalone tab: no props, operator types a Prop ID.
 * - Embedded (APFS Submitted row → L.11): pass `initialPropId` and `embedded`;
 *   it loads immediately and hides the search form.
 */
export default function LMSStatus({ initialPropId = '', embedded = false }) {
  const [propId, setPropId] = useState(initialPropId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [history, setHistory] = useState(null); // { propId, rows, error }
  const [storedAt, setStoredAt] = useState(null); // { at, by } when `result` comes from the DB, not APFS

  const loadHistory = async (id) => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/apfs/lms/history/${encodeURIComponent(id)}`);
      setHistory({ propId: id, rows: resp.data.data || [], error: null });
      return resp.data;
    } catch (err) {
      setHistory({ propId: id, rows: [], error: err.response?.data?.error || err.message });
      return null;
    }
  };

  // Embedded: show the last stored pull first; only call APFS when nothing
  // has been stored yet or the user presses "ดึงใหม่จาก APFS".
  const openStored = async (id) => {
    setLoading(true);
    const h = await loadHistory(id);
    const raw = h?.latest?.raw_response;
    if (raw?.data) {
      setResult({ success: true, data: raw.data, raw });
      setStoredAt({ at: h.latest.fetched_at, by: h.latest.fetched_by });
      setLoading(false);
    } else {
      setLoading(false);
      handleSearch(id);
    }
  };

  useEffect(() => {
    if (initialPropId) openStored(initialPropId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPropId]);

  const handleSearch = async (idOverride) => {
    const trimmed = String(typeof idOverride === 'string' ? idOverride : propId).trim();
    if (!trimmed) {
      setError('กรุณาระบุ Prop ID');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    setHistory(null);
    setStoredAt(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/apfs/lms/status`, { propId: trimmed });
      if (response.data.success) {
        setResult(response.data);
      } else {
        setError(response.data.error || response.data.message || 'ไม่พบข้อมูล');
        setResult(response.data);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      setError(msg);
      if (err.response?.data) setResult(err.response.data);
    } finally {
      setLoading(false);
      loadHistory(trimmed);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const data = result?.data;

  return (
    <div className="lms-status" style={embedded ? { padding: 0, maxWidth: 'none', boxShadow: 'none' } : undefined}>
      {!embedded && (
        <>
          <h2>L.11 LMS Status API</h2>
          <p className="lms-subtitle">ตรวจสอบสถานะ LMS (Loan Management System) ด้วย Prop ID</p>
        </>
      )}

      {embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                      padding: '8px 12px', marginBottom: 12, borderRadius: 6,
                      background: storedAt ? '#fff8e1' : '#e8f5e9', fontSize: 13 }}>
          <span>
            {loading
              ? 'กำลังโหลด…'
              : storedAt
                ? <>ข้อมูลที่บันทึกไว้ เมื่อ <b>{fmtDate(storedAt.at)}</b>{storedAt.by ? ` โดย ${storedAt.by}` : ''}</>
                : result ? 'ข้อมูลล่าสุดจาก APFS (บันทึกแล้ว)' : ''}
          </span>
          <button className="lms-btn-primary" style={{ padding: '6px 12px', fontSize: 13 }}
                  onClick={() => handleSearch(initialPropId)} disabled={loading}>
            🔄 ดึงใหม่จาก APFS
          </button>
        </div>
      )}

      {!embedded && (
      <div className="lms-form">
        <div className="lms-form-group">
          <label htmlFor="lms-propId">Prop ID</label>
          <div className="lms-input-row">
            <input
              id="lms-propId"
              type="text"
              value={propId}
              onChange={(e) => { setPropId(e.target.value); if (error) setError(''); }}
              onKeyDown={handleKeyDown}
              placeholder="เลขกรมธรรม์ชั่วคราว หรือ เลข refId"
              autoComplete="off"
            />
            <button
              className="lms-btn-primary"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? 'กำลังค้นหา...' : 'ค้นหา'}
            </button>
          </div>
          <p className="lms-hint">เลขกรมธรรม์ชั่วคราว หรือ เลข refId ที่ทาง FIN ส่งมาตอนสร้างสัญญากับ APFS ในครั้งแรก</p>
        </div>
      </div>
      )}

      {error && (
        <div className="lms-error">
          <span className="lms-error-icon">⚠️</span>
          {error}
        </div>
      )}

      {data && (
        <div className="lms-result">
          {/* Summary cards */}
          <div className="lms-cards">
            <div className="lms-card">
              <div className="lms-card-label">Prop ID</div>
              <div className="lms-card-value">{data.propId || '-'}</div>
            </div>
            <div className="lms-card">
              <div className="lms-card-label">Payment Method</div>
              <div className="lms-card-value">{data.method || '-'}</div>
            </div>
            <div className="lms-card">
              <div className="lms-card-label">งวดปัจจุบัน / ทั้งหมด</div>
              <div className="lms-card-value">{data.currentTerm ?? '-'} / {data.totalInstallmentTerm ?? '-'}</div>
            </div>
            <div className="lms-card lms-card-warn">
              <div className="lms-card-label">วันค้างชำระ (Overdue)</div>
              <div className="lms-card-value">{data.overdueDay ?? '-'} วัน</div>
            </div>
          </div>

          {/* Premium summary */}
          <div className="lms-section">
            <h3>ข้อมูลเบี้ยประกัน</h3>
            <div className="lms-info-grid">
              <div className="lms-info-row">
                <span className="lms-info-label">Gross Premium</span>
                <span className="lms-info-val">฿{fmtMoney(data.transGrossPremium)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">Discount</span>
                <span className="lms-info-val text-red">-฿{fmtMoney(data.transGrossPremiumDiscount)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">Gross After Discount</span>
                <span className="lms-info-val">฿{fmtMoney(data.transGrossPremiumAfterDiscount)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">Net Premium</span>
                <span className="lms-info-val">฿{fmtMoney(data.transNetPremuim)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">VAT</span>
                <span className="lms-info-val">฿{fmtMoney(data.vat)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">Stamp Duty</span>
                <span className="lms-info-val">฿{fmtMoney(data.transStampDuty)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">Down Payment</span>
                <span className="lms-info-val font-bold">฿{fmtMoney(data.totalDownAmount)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">Total Fee</span>
                <span className="lms-info-val">฿{fmtMoney(data.totalFeeAmount)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">Broker Service Fee</span>
                <span className="lms-info-val">฿{fmtMoney(data.brokerServiceFee)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">VAT Broker Service Fee</span>
                <span className="lms-info-val">฿{fmtMoney(data.vatBrokerServiceFee)}</span>
              </div>
              <div className="lms-info-row lms-info-highlight">
                <span className="lms-info-label">ลูกค้าจ่ายทั้งหมด</span>
                <span className="lms-info-val font-bold">฿{fmtMoney(data.totalAmountCustomerPay)}</span>
              </div>
              <div className="lms-info-row lms-info-highlight">
                <span className="lms-info-label">ยอดผ่อนทั้งหมด</span>
                <span className="lms-info-val font-bold">฿{fmtMoney(data.totalAmountForInstallment)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">ยอดงวดถัดไป</span>
                <span className="lms-info-val">฿{fmtMoney(data.nextInstallmentAmount)}</span>
              </div>
              <div className="lms-info-row">
                <span className="lms-info-label">งวดถัดไป (term)</span>
                <span className="lms-info-val">{data.nextInstallmentTerm ?? '-'}</span>
              </div>
            </div>
          </div>

          {/* Installments table */}
          {Array.isArray(data.installments) && data.installments.length > 0 && (
            <div className="lms-section">
              <h3>ตารางการผ่อนชำระ ({data.installments.length} งวด)</h3>
              <div className="lms-table-wrapper">
                <table className="lms-table">
                  <thead>
                    <tr>
                      <th>งวดที่</th>
                      <th>วันครบกำหนด</th>
                      <th>วันที่ชำระ</th>
                      <th>สถานะ</th>
                      <th className="text-right">ยอดชำระ (฿)</th>
                      <th className="text-right">ค่าธรรมเนียม (฿)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.installments.map((inst, i) => (
                      <tr key={i} className={inst.term === data.currentTerm ? 'lms-row-current' : ''}>
                        <td className="text-center font-bold">{inst.term}</td>
                        <td>{fmtDate(inst.dueDate)}</td>
                        <td>{fmtDate(inst.paidAt)}</td>
                        <td><PaymentStatusBadge status={inst.paymentStatus} /></td>
                        <td className="text-right">฿{fmtMoney(inst.termOfPayment)}</td>
                        <td className="text-right">฿{fmtMoney(inst.installmentFeeAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {history && (
        <div className="lms-section">
          <h3>ประวัติการดึงสถานะ L.11 ({history.rows.length} ครั้ง)</h3>
          {history.error ? (
            <div className="lms-error">
              <span className="lms-error-icon">⚠️</span>
              โหลดประวัติไม่ได้: {history.error} (ยังไม่ได้รัน migration 013?)
            </div>
          ) : history.rows.length === 0 ? (
            <p className="lms-hint">ยังไม่มีประวัติ</p>
          ) : (
            <div className="lms-table-wrapper">
              <table className="lms-table">
                <thead>
                  <tr>
                    <th>เวลาที่ดึง</th>
                    <th>โดย</th>
                    <th>ผล</th>
                    <th className="text-center">งวดปัจจุบัน</th>
                    <th className="text-center">จ่ายแล้ว</th>
                    <th className="text-center">ค้าง (วัน)</th>
                    <th>ดิวงวดถัดไป</th>
                    <th className="text-right">ยอดงวดถัดไป (฿)</th>
                  </tr>
                </thead>
                <tbody>
                  {history.rows.map((h) => (
                    <tr key={h.id}>
                      <td>{fmtDate(h.fetched_at)}</td>
                      <td><small>{h.fetched_by || h.source || '-'}</small></td>
                      <td>
                        {h.success
                          ? <span className="lms-badge badge-paid">OK</span>
                          : <span className="lms-badge badge-overdue" title={h.error_message || ''}>{h.http_status || 'ERR'}</span>}
                      </td>
                      <td className="text-center">{h.success ? `${h.current_term ?? '-'} / ${h.total_installment_term ?? '-'}` : '-'}</td>
                      <td className="text-center">{h.success ? (h.paid_terms ?? '-') : '-'}</td>
                      <td className="text-center">{h.success ? (h.overdue_day ?? '-') : '-'}</td>
                      <td>{h.success ? fmtDate(h.next_due_date) : <small>{h.error_message || '-'}</small>}</td>
                      <td className="text-right">{h.success ? fmtMoney(h.next_installment_amount) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="lms-raw-section">
          <button className="lms-btn-toggle" onClick={() => setShowRaw(!showRaw)}>
            {showRaw ? 'ซ่อน' : 'แสดง'} Raw Response
          </button>
          {showRaw && (
            <pre className="lms-raw">{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}
